/**
 * Phone Health (Oct 27, 2026): types + helpers for /phone-health.
 *
 * All numbers come from the phone_health_report(from, to) RPC
 * (supabase/migrations/20261027_phone_health.sql, answered rule updated in
 * 20261027c_call_amd_verdict.sql). Its rules:
 *   - an inbound call is ANSWERED when it is 'completed' and lasted over 30 s
 *     (twilio-inbound-call's WHISPER_MAX_SECONDS), or was bridged and Twilio's
 *     answering-machine detector heard a person pick up; any other forwarded
 *     call that drops inside 30 s is a miss, as the call handler treats it;
 *   - a call still 'ringing' / 'menu' after an hour is STUCK;
 *   - a text with no delivery receipt after an hour counts as "no receipt".
 * Twilio's alerts and number settings are copied in hourly by the
 * twilio-health-sync edge function.
 */
import { supabase } from "./supabase";

export type Outcome = "answered" | "missed" | "in_progress" | "stuck";

export interface PersonRung { member_id: string | null; name: string; rung: number; answered: number; missed: number }
export interface DayCalls { day: string; total: number; answered: number; missed: number }
export interface RecentCall {
  at: string; from: string; outcome: Outcome; duration: number | null; left_vm: boolean; texted_back: boolean;
  deal_id: string | null; homeowner: string | null; rang: string | null;
}
export interface StuckCall { at: string; from: string; status: string; rang: string | null; deal_id: string | null }
export interface RepOutbound {
  member_id: string | null; name: string; calls: number; talked: number; short: number;
  no_answer: number; failed: number; talk_minutes: number;
}
export interface LineTexts {
  line: "customer" | "team" | "crew"; sent: number; delivered: number; failed: number; no_receipt: number;
  received: number; codes: { code: string; n: number }[];
}
export interface FailedText {
  at: string; line: string; to: string; tag: string | null; code: string | null; error: string | null;
  status: string | null; who: string | null;
}
export interface AlertRow {
  at: string; code: string | null; level: string | null; url: string | null; text: string | null;
  resource_sid: string | null; to: string | null; tag: string | null; who: string | null;
}
export interface NumberConfig {
  number: string; name: string | null; voice_url: string | null; sms_url: string | null; status_callback: string | null;
  checked_at: string; expected_voice: string | null; expected_sms: string | null;
}
export interface PhoneHealthReport {
  window: { from: string; to: string };
  generated_at: string;
  inbound: {
    outside_total: number; answered: number; missed: number; in_progress: number; stuck: number;
    voicemails: number; missed_no_vm: number; texted_back: number; known_callers: number; unknown_callers: number;
    staff_calls: number; line_tests: number;
    by_day: DayCalls[]; by_person: PersonRung[]; stuck_calls: StuckCall[]; recent: RecentCall[];
  };
  outbound: { total: number; talked: number; short: number; no_answer: number; failed: number; no_rep: number; by_rep: RepOutbound[] };
  texts: LineTexts[];
  failed_texts: FailedText[];
  alerts: { total: number; by_code: { code: string | null; n: number; last: string }[]; recent: AlertRow[] };
  numbers: NumberConfig[];
  settings: Record<string, unknown>;
  synced: { alerts_at: string | null; numbers_at: string | null };
}

export type RangeKey = "today" | "7d" | "30d" | "90d";
export const RANGES: { key: RangeKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

/** Window start for a range key. "today" starts at midnight Eastern. */
export function rangeStart(key: RangeKey, now: Date = new Date()): Date {
  if (key === "today") {
    // Midnight in America/New_York, whatever the browser's zone.
    const etDay = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(now); // YYYY-MM-DD
    const offsetHours = etOffsetHours(now);
    return new Date(`${etDay}T00:00:00${offsetHours}`);
  }
  const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
  return new Date(now.getTime() - days * 86_400_000);
}

/** "-04:00" in daylight time, "-05:00" in standard time. */
export function etOffsetHours(at: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", timeZoneName: "shortOffset" }).formatToParts(at);
  const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT-5";
  const m = name.match(/GMT([+-]\d+)/);
  const h = m ? parseInt(m[1], 10) : -5;
  const sign = h < 0 ? "-" : "+";
  return `${sign}${String(Math.abs(h)).padStart(2, "0")}:00`;
}

export async function fetchPhoneHealth(from: Date, to: Date): Promise<PhoneHealthReport> {
  const { data, error } = await supabase.rpc("phone_health_report", { p_from: from.toISOString(), p_to: to.toISOString() });
  if (error) throw new Error(error.message);
  return data as PhoneHealthReport;
}

/** Share as a whole percent, or null when there is nothing to divide. */
export function pct(part: number, whole: number): number | null {
  if (!whole) return null;
  return Math.round((part / whole) * 100);
}

/** The edge function a webhook URL points at, e.g. "twilio-inbound-call". */
export function fnFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/functions\/v1\/([^/?#]+)/);
  return m ? m[1] : url;
}

export const LINE_LABEL: Record<string, string> = {
  customer: "Customer line (470) 823-8816",
  team: "Team line (470) 937-9490",
  crew: "Crew line (470) 873-9951",
};

/** Plain-English meaning of the Twilio error codes we actually see. */
const CODE_MEANING: Record<string, string> = {
  "21211": "Not a valid phone number",
  "21266": "Sent to the same number it came from",
  "21408": "No permission to text that region",
  "21610": "They replied STOP, so Twilio blocks texts to them",
  "21614": "Not a mobile number",
  "21617": "Text too long for one message",
  "30003": "Phone unreachable (off or out of service)",
  "30004": "Blocked by the recipient or their carrier",
  "30005": "Number doesn't exist",
  "30006": "Landline, or a number that can't get texts",
  "30007": "Carrier filtered it as spam",
  "30008": "Carrier error, reason unknown",
  "30034": "Number not registered for business texting",
  "11200": "Our webhook didn't answer Twilio",
  "11205": "Couldn't connect to our webhook",
  "12100": "Our webhook sent back something Twilio couldn't read",
  "13224": "Dialed a number that isn't valid",
  "13227": "No permission to call that country",
};

export function codeMeaning(code: string | null | undefined): string | null {
  if (!code) return null;
  return CODE_MEANING[String(code)] ?? null;
}

export function codeLink(code: string | null | undefined): string | null {
  if (!code || !/^\d+$/.test(String(code))) return null;
  return `https://www.twilio.com/docs/api/errors/${code}`;
}

/** Health flags shown at the top of the page, worst first. */
export interface HealthFlag { tone: "rose" | "amber" | "slate"; text: string }

export function healthFlags(r: PhoneHealthReport): HealthFlag[] {
  const flags: HealthFlag[] = [];
  if (r.inbound.stuck > 0) {
    flags.push({ tone: "rose", text: `${r.inbound.stuck} call${r.inbound.stuck === 1 ? "" : "s"} never finished (still "ringing" after an hour). The call's last step from Twilio didn't reach us.` });
  }
  for (const n of r.numbers) {
    const v = fnFromUrl(n.voice_url);
    const s = fnFromUrl(n.sms_url);
    if (n.expected_voice && v !== n.expected_voice) flags.push({ tone: "rose", text: `${n.name ?? n.number}: calls go to ${v ?? "nothing"}, expected ${n.expected_voice}.` });
    if (n.expected_sms && s !== n.expected_sms) flags.push({ tone: "rose", text: `${n.name ?? n.number}: texts go to ${s ?? "nothing"}, expected ${n.expected_sms}.` });
  }
  const webhookErrors = r.alerts.by_code.filter((c) => c.code && /^1[12]\d{3}$/.test(c.code)).reduce((a, c) => a + c.n, 0);
  if (webhookErrors > 0) flags.push({ tone: "rose", text: `${webhookErrors} webhook error${webhookErrors === 1 ? "" : "s"} from Twilio (our functions didn't answer cleanly).` });
  const missedNoVm = r.inbound.missed_no_vm;
  if (missedNoVm > 0) flags.push({ tone: "amber", text: `${missedNoVm} missed call${missedNoVm === 1 ? "" : "s"} with no voicemail left.` });
  const failed = r.texts.reduce((a, t) => a + t.failed, 0);
  if (failed > 0) flags.push({ tone: "amber", text: `${failed} text${failed === 1 ? "" : "s"} failed to deliver.` });
  const noReceipt = r.texts.reduce((a, t) => a + t.no_receipt, 0);
  if (noReceipt > 0) flags.push({ tone: "slate", text: `${noReceipt} text${noReceipt === 1 ? "" : "s"} never got a delivery receipt.` });
  const s = r.settings;
  if (String(s.call_menu_mode ?? "on") === "off") flags.push({ tone: "slate", text: "Call menu is off: unknown callers ring the general line directly." });
  const amd = String(s.call_forward_amd ?? "on");
  if (amd !== "on") flags.push({ tone: "slate", text: `Voicemail detection on forwarded cells is "${amd}".` });
  const tb = String(s.missed_call_textback_mode ?? "on");
  if (tb !== "on") flags.push({ tone: "slate", text: `Missed-call text-back is "${tb}".` });
  return flags;
}

/** "5 min ago", "3 h ago", "Oct 26". */
export function ago(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "never";
  const ms = now.getTime() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" });
}
